/**
 * mapView — the one definition of "where a map opens".
 *
 * `{ longitude: 133.7751, latitude: -25.2744 }` (central Australia) used to be
 * copy-pasted into five components, three of which never consulted the org's own
 * configured view at all — so an admin editing a Kimberley org, or any org outside
 * Australia, got a continent-wide view of the wrong place.
 *
 * NOTE ON TERMS: `initial_map_view` is a **centre and zoom**, not a bounding box.
 * There is no bbox anywhere in `/api/v2/config/`. It describes the same visible
 * area; the distinction only matters if you go looking for a bbox field.
 */

/** An org's initial map view, as stored in `config.initial_map_view`. */
export interface MapView {
  longitude?: number;
  latitude?: number;
  zoom?: number;
  pitch?: number;
}

/**
 * Last-resort view when an org has no `initial_map_view` — most CultureMap orgs
 * are Australian, so a continent-wide view of Australia beats a view of the
 * Atlantic. Every map should exhaust the org's own config before reaching for it.
 */
export const DEFAULT_MAP_VIEW: Required<MapView> = {
  longitude: 133.7751,
  latitude: -25.2744,
  zoom: 3.5,
  pitch: 0
};

/**
 * Fills the gaps in a partial view from {@link DEFAULT_MAP_VIEW}, one field at a
 * time — an org that configured a centre but no zoom keeps its centre.
 *
 * IMPORTANT for admin surfaces: pass the view of the org **being edited**, not
 * `useConfig()`. `useConfig()` is the signed-in user's own config, so a superuser
 * editing another org would otherwise be shown their own map view. The console
 * already makes this distinction for `primaryColor` in `TableStyleDialog`.
 */
export const resolveMapView = (...views: Array<MapView | null | undefined>): Required<MapView> => {
  const pick = <K extends keyof MapView>(key: K): number =>
    (views.find((v) => typeof v?.[key] === "number")?.[key] as number | undefined) ?? DEFAULT_MAP_VIEW[key];
  return { longitude: pick("longitude"), latitude: pick("latitude"), zoom: pick("zoom"), pitch: pick("pitch") };
};

/**
 * A view that frames a bounding box: its centre, and the closest zoom at which
 * the whole box still fits.
 *
 * An approximation, deliberately. MapLibre's own `fitBounds` needs the pixel size
 * of a viewport that doesn't exist yet at the point we want this — when an
 * operator has just drawn a boundary and the map that would show it hasn't
 * mounted. The `- 0.5` is the padding that keeps the shape off the edges, and the
 * 170° vertical span approximates Mercator's usable range.
 */
export const viewForBounds = (bounds?: [number, number, number, number] | null): MapView | undefined => {
  if (!bounds) return undefined;
  const [minLng, minLat, maxLng, maxLat] = bounds;
  const lngSpan = Math.abs(maxLng - minLng);
  const latSpan = Math.abs(maxLat - minLat);

  // A degenerate box (a single point, or a boundary of one vertex) has no span to
  // fit, so there is no meaningful zoom — go close enough to see the locality.
  const zoomFor = (span: number, worldSpan: number) => (span > 0 ? Math.log2(worldSpan / span) : 14);
  const zoom = Math.min(Math.max(Math.min(zoomFor(lngSpan, 360), zoomFor(latSpan, 170)) - 0.5, 0), 16);

  return {
    longitude: (minLng + maxLng) / 2,
    latitude: (minLat + maxLat) / 2,
    zoom: Math.round(zoom * 100) / 100
  };
};
